export const REBA_FIELDS = [
  'step_1', // nuque
  'step_2', // tronc
  'step_3', // jambes
  'step_4', // score table A
  'step_7', // epaule
  'step_8', // coude
  'step_9', // poignet
  'step_10', // score table B
  'step_13', // score table C
];

export const REBA_THRESHOLDS = {
  'step_1': [1, 2, 3], // nuque
  'step_2': [1, 3, 4], // tronc
  'step_3': [1, 2, 3], // jambes
  'step_4': [1, 5, 8], // score table A
  'step_7': [1, 3, 4], // epaule
  'step_8': [1, 2, 1000], // coude
  'step_9': [1, 2, 3], // poignet
  'step_10': [1, 4, 7], // score table B
  'step_13': [1, 2, 4, 7, 9], // score table C
};

export const REBA_THRESHOLDS_INFLUXDB = Object.entries(REBA_THRESHOLDS).map(([key, values]) => ({
  _field: key,
  threshold: values[Math.floor(values.length / 2)] ?? 0.0,
}));

export const getBarColor = (context, key) => {
  const value = context.raw?.y ?? 0;

  if (REBA_THRESHOLDS[key].length === 3) {
    if (value >= REBA_THRESHOLDS[key][2]) return 'rgba(255, 99, 132, 0.8)'; // Red
    else if (value >= REBA_THRESHOLDS[key][1]) return 'rgba(255, 206, 86, 0.8)'; // Yellow
    else return 'rgba(75, 255, 192, 0.8)'; // Green
  }
  else {
    if (value >= REBA_THRESHOLDS[key][4]) return 'rgba(255, 120, 150, 0.9)'; // Bright red
    else if (value >= REBA_THRESHOLDS[key][3]) return 'rgba(255, 99, 132, 0.8)'; // Red
    else if (value >= REBA_THRESHOLDS[key][2]) return 'rgba(255, 150, 86, 0.8)'; // Orange
    else if (value >= REBA_THRESHOLDS[key][1]) return 'rgba(255, 206, 86, 0.8)'; // Yellow
    else return 'rgba(75, 255, 192, 0.8)'; // Green
  }
};

export const getExpositionColor = (context) => {
  const value = context.raw ?? 0;

  if (value >= 70) return 'rgba(255, 99, 132, 0.8)'; // Red
  else if (value >= 30) return 'rgba(255, 206, 86, 0.8)' // Yellow
  else return 'rgba(75, 255, 192, 0.8)'; // Green
};

export const createDateRange = (startTimestamp, stopTimestamp) => {
  const durationSec = (stopTimestamp - startTimestamp) / 1000;
  let window, unit, stepSize;
  if (durationSec <= 5 * 60) // 300s
    window = '1s', unit = 'second', stepSize = 2; // each 2 s
  else if (durationSec <= 15 * 60) // 900s
    window = '3s', unit = 'minute', stepSize = 1; // each 1 min
  else if (durationSec <= 30 * 60) // 30min
    window = '10s', unit = 'minute', stepSize = 3; // each 3 min
  else if (durationSec <= 2 * 60 * 60) // 2h
    window = '2m', unit = 'minute', stepSize = 10; // each 10 min
  else if (durationSec <= 6 * 60 * 60) // 6h
    window = '5m', unit = 'minute', stepSize = 30; // each 30 min
  else if (durationSec <= 24 * 60 * 60) // 24h
    window = '15m', unit = 'minute', stepSize = 60; // each 1 h
  else if (durationSec <= 3 * 24 * 60 * 60) // 3d
    window = '1h', unit = 'hour', stepSize = 12; // each 12 h
  else if (durationSec <= 7 * 24 * 60 * 60) // 1w
    window = '3h', unit = 'hour', stepSize = 24; // each 24 h
  else if (durationSec <= 30 * 24 * 60 * 60) // 1month
    window = '1d', unit = 'day', stepSize = 2; // each 2 d
  else
    window = '1w', unit = 'week', stepSize = 1; // each 1 w

  return {
    start: startTimestamp,
    stop: stopTimestamp,
    window: window,
    unit: unit,
    stepSize: stepSize,
  };
};

export const getQuery = (workstationId, fields, window) => {
  return `
    import "array"
    import "strings"
    import "regexp"
    
    fields = ${JSON.stringify(fields)}
    pat    = "^(" + strings.joinStr(arr: fields, v: "|") + ")$"
    re     = regexp.compile(v: pat)
    
    base =
      from(bucket: "engine")
        |> range(start: v.timeRangeStart, stop: v.timeRangeStop)
        |> filter(fn: (r) => r._measurement == "reba_score")
        |> filter(fn: (r) => r._field == "workstation_id" or r._field =~ re)
        |> pivot(rowKey: ["_time"], columnKey: ["_field"], valueColumn: "_value")
        |> filter(fn: (r) => r.workstation_id == ${workstationId})
        |> drop(columns: ["workstation_id"])
    
    // \`cols\`, \`present\`
    cols =
      base
        |> columns()
        |> keep(columns: ["_value"])
        |> distinct()
        |> findColumn(fn: (key) => true, column: "_value")
    
    present = array.filter(arr: fields, fn: (x) => contains(value: x, set: cols))
    missing = array.filter(arr: fields, fn: (x) => not contains(value: x, set: cols))
    
    default = -1
    
    // Timestamps "référence"
    times =
      base
        |> keep(columns: ["_time"])
        |> distinct()
    
    make = (col) =>
      base
        |> duplicate(column: col, as: "_value")
        |> map(fn: (r) => ({ r with _value: if exists r._value then r._value else default }))
        |> keep(columns: ["_time","_value"])
        |> set(key: "_field", value: col)
    
    // Série constante pour un champ absent
    makeConst = (col) =>
      times
        |> map(fn: (r) => ({ r with _value: default }))
        |> set(key: "_field", value: col)
    
    // On unionne les champs présents + les champs absents
    u = union(
      tables: array.map(arr: present, fn: (x) => make(col: x))
        |> array.concat(v: array.map(arr: missing, fn: (x) => makeConst(col: x)))
    )
    
    meansLong = u
        |> group(columns: ["_field"])
        |> aggregateWindow(every: ${window}, fn: mean, createEmpty: false)
    
    maxsLong = u
        |> group(columns: ["_field"])
        |> aggregateWindow(every: ${window}, fn: max, createEmpty: false)
        // renomme dynamiquement les champs max en step_X_max
        |> map(fn: (r) => ({ r with _field: r._field + "_max" }))
        |> map(fn: (r) => ({ r with _value: float(v: r._value) }))
    
    // union au format long -> pivot en colonnes step_X (mean) et step_X_max (max)
    union(
      tables: [meansLong] |> array.concat(v: [maxsLong])
    )
      |> group(columns: [])
      |> pivot(rowKey: ["_time"], columnKey: ["_field"], valueColumn: "_value")
      |> rename(columns: {_time: "time"})
  `;
};

export const getExpositionQuery = (workstationId, fieldsThresholds) => {
  return `
    import "array"
    import j "join"
    import "math"
    
    //thresholds = array.from(rows: [
    //  {_field: "step_1", threshold: 2.0},
    //  {_field: "step_2", threshold: 0.0},
    //  {_field: "step_3", threshold: 0.0},
    //])
    
    thresholds = array.from(rows: ${JSON.stringify(fieldsThresholds)})
    
    base = from(bucket: "engine")
      |> range(start: v.timeRangeStart, stop: v.timeRangeStop)
      |> filter(fn: (r) => r._measurement == "reba_score")
      |> filter(fn: (r) => r._field == "workstation_id" or r._field =~ /^step_/)
      |> keep(columns: ["_time","_field","_value"])
    
    // filtre sur workstation_id
    workstationFilter = base
      |> filter(fn: (r) => r._field == "workstation_id" and r._value == ${workstationId})
      |> keep(columns: ["_time"])  // clé(s) de jointure
    
    // selectionner les steps
    stepsOnly = base
      |> filter(fn: (r) => r._field =~ /^step_/)
      |> group()
      |> sort(columns: ["_time"], desc: false)
    
    data = j.inner(left: stepsOnly, right: workstationFilter, on: (l, r) => l._time == r._time, as: (l, r) => l)
    
    // associe la ligne de seuil correspondante à chaque field (join sur _field)
    joined = join(tables: {d: data, t: thresholds}, on: ["_field"])
    
    // marque 1 si dépassement, sinon 0
    flagged = joined
      |> map(fn: (r) => ({r with exceed: if r._value >= r.threshold then 1 else 0, total: 1}))
      |> keep(columns: ["_time","_field","exceed","total"])
    
    // compte le nombre de dépassements par field (sur toute la période)
    flagged
      |> group(columns: ["_field"])
      |> reduce(identity: {exceed: 0, total: 0}, fn: (r, accumulator) => ({ exceed: accumulator.exceed + r.exceed, total: accumulator.total + r.total}))
      |> map(fn: (r) => ({r with percent: math.round(x: float(v: r.exceed) / float(v: r.total) * 100.0)}))
  `;
};

export const formatSeriesByField = (data, fieldsNames, fill = 0) => {
  const expandedFieldsNames = fieldsNames.flatMap(item => item.startsWith("step_") ? [item, item + "_max"] : [item]);
  const out = Object.fromEntries(expandedFieldsNames.map(k => [k, []]));

  for (const row of data) {
    for (const k of expandedFieldsNames) {
      const v = row[k];
      out[k].push({time: row.time, value: typeof v === "number" ? v : fill});
    }
  }
  return out;
};

export const createPhases = (seriesData, seriesNames) => {
  const phasesData = buildPhases1s(seriesData, seriesNames);

  return Object.fromEntries(
    phasesData.flatMap(p => {
      const mid = (p.start + p.end) / 2;

      const box = [p.id, {
        type: 'box',
        xScaleID: 'x',
        yScaleID: 'y',
        xMin: p.start,
        xMax: p.end,
        yMin: 'min',
        yMax: 'max',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderColor: p.border ?? 'rgba(255,255,255,0.5)',
        borderWidth: 0,
        drawTime: 'beforeDatasetsDraw',
      }];

      const label = [`${p.id}_label`, {
        type: 'label',
        xScaleID: 'x',
        yScaleID: 'y',
        xValue: () => (p.start + p.end) / 2,
        yValue: (ctx) => ctx.chart.scales.y?.max,
        position: { x: 'center', y: 'start' },
        content: () => p.label,
        backgroundColor: 'rgba(0,0,0,0.35)',
        color: '#ddd',
        padding: 4,
        borderRadius: 4,
        font: { weight: '600' },
        drawTime: 'afterDatasetsDraw',
        clip: { x: true, y: true },
        display: (ctx) => {
          const chart = ctx?.chart;
          const ca = chart?.chartArea;
          const xScale = chart?.scales?.x;
          if (!chart || !ca || !xScale) return false;
          if (!Number.isFinite(xScale.min) || !Number.isFinite(xScale.max)) return false;

          const text = p.label || '';
          // mesurer le texte seulement quand le contexte est prêt
          const padX = 12; // padding horizontal total
          const w = chart.ctx?.measureText ? chart.ctx.measureText(text).width + padX : 0;

          const xStart = xScale.getPixelForValue(p.start);
          const xEnd   = xScale.getPixelForValue(p.end);
          if (!Number.isFinite(xStart) || !Number.isFinite(xEnd)) return false;

          const visLeft  = Math.max(ca.left, Math.min(xStart, xEnd));
          const visRight = Math.min(ca.right, Math.max(xStart, xEnd));
          const visWidth = visRight - visLeft;

          // n'affiche rien si le label ne tient pas entièrement dans la box visible
          return visWidth > 0 && w <= visWidth;
        },
      }];

      return [box, label];
    })
  );
};

export const buildPhases1s = (seriesDict, labels) => {
  const phases = [];

  for (const seriesName of Object.keys(seriesDict)) {
    if (seriesName.startsWith("step_")) continue;

    const series = seriesDict[seriesName];
    let inPhase = false;
    let startTime = null;

    for (let i = 0; i < series.length; i++) {
      const { time, value } = series[i];

      if (!inPhase && value > 0) {
        inPhase = true;
        startTime = time;
      } else if (inPhase && value <= 0) {
        const endExclusive = time;
        phases.push({
          id: `phase_${phases.length + 1}`,
          start: new Date(startTime).getTime(),
          end: new Date(endExclusive).getTime(),
          label: labels[seriesName],
        });
        inPhase = false;
        startTime = null;
      }
    }

    // Fin de série encore "en phase"
    if (inPhase && startTime != null) {
      const lastTime = series[series.length - 1].time;
      const endPlus1s = new Date(new Date(lastTime).getTime() + 1000).toISOString();
      phases.push({
        id: `phase_${phases.length + 1}`,
        start: new Date(startTime).getTime(),
        end: new Date(endPlus1s).getTime(),
        label: labels[seriesName],
      });
    }
  }

  return phases;
};